export interface FredSeries {
  id: string;
  slug: string;
  name: string;
  shortName: string;
  description: string;
  seoTitle: string;
  seoDescription: string;
}

export interface MaterialCategory {
  slug: string;
  name: string;
  shortName: string;
  description: string;
  series: FredSeries[];
  primarySeriesId: string;
  relatedProductTypes: string[];
  seoTitle: string;
  seoDescription: string;
}

export const MATERIAL_CATEGORIES: MaterialCategory[] = [
  {
    slug: "containerboard",
    name: "Corrugated & Containerboard",
    shortName: "Containerboard",
    description:
      "Producer Price Index data for corrugated packaging materials including finished boxes, raw sheets, linerboard, corrugating medium, and recycled fiber (OCC).",
    series: [
      {
        id: "PCU32221132221102",
        slug: "shipping-containers",
        name: "Corrugated Shipping Containers",
        shortName: "Shipping Containers",
        description:
          "Wholesale price of finished corrugated boxes and containers",
        seoTitle: "Corrugated Shipping Container Prices | PPI Index & Trends",
        seoDescription:
          "Track the Producer Price Index for corrugated shipping containers. Monthly wholesale box pricing data from FRED with historical charts and analysis.",
      },
      {
        id: "PCU32221132221105",
        slug: "sheets-and-rolls",
        name: "Corrugated Sheets & Rolls",
        shortName: "Sheets & Rolls",
        description:
          "Price of raw corrugated board before converting into boxes",
        seoTitle: "Corrugated Sheets & Rolls Prices | PPI Index & Trends",
        seoDescription:
          "Track the Producer Price Index for corrugated sheets and rolls. Monthly raw corrugated board pricing data from FRED.",
      },
      {
        id: "WPU0914",
        slug: "linerboard",
        name: "Linerboard (Paperboard PPI)",
        shortName: "Linerboard",
        description:
          "Proxy for kraft and recycled linerboard pricing, the flat layers of containerboard",
        seoTitle: "Linerboard Price Index | Paperboard PPI Data & Trends",
        seoDescription:
          "Track the Paperboard PPI as a proxy for kraft and recycled linerboard pricing. Monthly data from FRED with historical trends.",
      },
      {
        id: "PCU322211322211",
        slug: "corrugated-medium",
        name: "Corrugated Medium",
        shortName: "Medium",
        description:
          "Proxy for corrugating medium, the fluted layer between linerboard sheets",
        seoTitle: "Corrugated Medium Price Index | PPI Data & Trends",
        seoDescription:
          "Track the Producer Price Index for corrugating medium. Monthly pricing data for the fluted layer in containerboard from FRED.",
      },
      {
        id: "PCU42993042993033",
        slug: "occ",
        name: "OCC (Recycled Fiber)",
        shortName: "OCC",
        description:
          "Old Corrugated Containers — the primary recovered fiber feedstock for recycled containerboard mills",
        seoTitle: "OCC & Recycled Fiber Price Index | Material Recyclers PPI",
        seoDescription:
          "Track OCC (Old Corrugated Containers) pricing via the Material Recyclers PPI. Monthly recycled fiber pricing data from FRED.",
      },
    ],
    primarySeriesId: "PCU32221132221102",
    relatedProductTypes: [
      "gaylord-boxes",
      "shipping-boxes",
      "moving-boxes",
      "cardboard-bales",
    ],
    seoTitle: "Corrugated & Containerboard Price Index | PPI Data & Trends",
    seoDescription:
      "Track corrugated and containerboard PPI data from FRED. Monthly Producer Price Index for shipping containers, linerboard, corrugating medium, and OCC recycled fiber.",
  },
  {
    slug: "lumber",
    name: "Lumber & Wood Products",
    shortName: "Lumber",
    description:
      "Producer Price Index data for wholesale lumber, tracking softwood and hardwood pricing that drives wood packaging costs.",
    series: [
      {
        id: "WPU0811",
        slug: "lumber",
        name: "Lumber",
        shortName: "Lumber",
        description:
          "Wholesale lumber price index covering softwood and hardwood",
        seoTitle: "Lumber Price Index | Wholesale PPI Data & Trends",
        seoDescription:
          "Track the wholesale lumber Producer Price Index from FRED. Monthly softwood and hardwood pricing data with historical charts.",
      },
    ],
    primarySeriesId: "WPU0811",
    relatedProductTypes: ["pallets", "wood-crates", "wooden-spools", "lumber"],
    seoTitle: "Lumber & Wood Price Index | PPI Data & Trends",
    seoDescription:
      "Track lumber and wood PPI data from FRED. Monthly Producer Price Index for wholesale lumber pricing.",
  },
  {
    slug: "plastics",
    name: "Plastics & Resin Products",
    shortName: "Plastics",
    description:
      "Producer Price Index data for plastics products and resin, tracking wholesale pricing for raw plastics materials used in industrial packaging.",
    series: [
      {
        id: "WPU0721",
        slug: "plastics-products",
        name: "Plastics Products",
        shortName: "Plastics",
        description: "Wholesale plastics product price index",
        seoTitle: "Plastics Products Price Index | PPI Data & Trends",
        seoDescription:
          "Track the wholesale plastics products Producer Price Index from FRED. Monthly resin and plastics pricing data.",
      },
      {
        id: "WPU072104",
        slug: "plastics-packaging",
        name: "Plastics Packaging Materials",
        shortName: "Packaging Materials",
        description:
          "Raw plastics packaging materials including film, wrapping, and bulk packaging resins",
        seoTitle:
          "Plastics Packaging Materials Price Index | PPI Data & Trends",
        seoDescription:
          "Track the Producer Price Index for plastics packaging materials. Monthly pricing data for packaging-grade resins and films from FRED.",
      },
    ],
    primarySeriesId: "WPU0721",
    relatedProductTypes: [
      "ibc-totes",
      "plastic-drums",
      "plastic-pallets",
      "plastic-crates",
      "bulk-bags",
    ],
    seoTitle: "Plastics & Resin Price Index | PPI Data & Trends",
    seoDescription:
      "Track plastics and resin PPI data from FRED. Monthly Producer Price Index for wholesale plastics products and packaging materials.",
  },
  {
    slug: "steel",
    name: "Steel & Metal Products",
    shortName: "Steel",
    description:
      "Producer Price Index data for steel mill products, tracking wholesale pricing for steel used in metal drums, containers, and industrial packaging.",
    series: [
      {
        id: "WPU1017",
        slug: "steel-mill",
        name: "Steel Mill Products",
        shortName: "Steel",
        description:
          "Wholesale steel mill product price index covering sheet, strip, and plate",
        seoTitle: "Steel Mill Products Price Index | PPI Data & Trends",
        seoDescription:
          "Track the steel mill products Producer Price Index from FRED. Monthly wholesale steel pricing data with historical charts.",
      },
    ],
    primarySeriesId: "WPU1017",
    relatedProductTypes: ["metal-drums"],
    seoTitle: "Steel & Metal Price Index | PPI Data & Trends",
    seoDescription:
      "Track steel and metal PPI data from FRED. Monthly Producer Price Index for steel mill products used in metal drums and industrial packaging.",
  },
] as const satisfies MaterialCategory[];

export type MaterialCategorySlug = (typeof MATERIAL_CATEGORIES)[number]["slug"];

export const MATERIAL_CATEGORY_SLUGS: MaterialCategorySlug[] =
  MATERIAL_CATEGORIES.map((c) => c.slug);

export function getAllFredSeriesIds(): string[] {
  const ids = new Set<string>();
  for (const cat of MATERIAL_CATEGORIES) {
    for (const s of cat.series) {
      ids.add(s.id);
    }
  }
  return Array.from(ids);
}

export function getMaterialCategory(
  slug: string,
): MaterialCategory | undefined {
  return MATERIAL_CATEGORIES.find((c) => c.slug === slug);
}

export function getSeriesInCategory(
  categorySlug: string,
  seriesSlug: string,
): FredSeries | undefined {
  const cat = getMaterialCategory(categorySlug);
  if (!cat) return undefined;
  return cat.series.find((s) => s.slug === seriesSlug);
}

export function getCategoryForSeries(
  seriesId: string,
): MaterialCategory | undefined {
  return MATERIAL_CATEGORIES.find((c) =>
    c.series.some((s) => s.id === seriesId),
  );
}

export function getAllSeriesParams(): Array<{
  slug: string;
  seriesSlug: string;
}> {
  const params: Array<{ slug: string; seriesSlug: string }> = [];
  for (const cat of MATERIAL_CATEGORIES) {
    if (cat.series.length < 2) continue;
    for (const s of cat.series) {
      params.push({ slug: cat.slug, seriesSlug: s.slug });
    }
  }
  return params;
}
